import { useCallback, useEffect, useRef, useState } from 'react';
import { sparkToast } from '@spark';
import { announceIfFreeUsageExhausted, get_base_url } from '@/shared/http/client';
import { formatApiErrorDetail } from '@/shared/utils/formatApiErrorDetail';
/**
 * Hook that owns the lifecycle of the GPU-backed summarization vLLM session
 * for a single Playground page (one llm_usecase).
 *
 * Status machine:
 *   idle      -> Start GPU button shown
 *   starting  -> backend acked Start, polling /status until vLLM /v1/models is up
 *   running   -> ready to accept inference; auto-stop countdown ticks down
 *   stopping  -> backend acked Stop, transitioning back to idle
 *   error     -> last operation failed; user can retry
 *
 * The hard 2-hour auto-stop is enforced by the RabbitMQ message TTL on
 * the backend; the local countdown is only there to inform the user.
 *
 * Source of truth
 * ---------------
 * Both ``/start`` and ``/status`` return the canonical ``started_at`` /
 * ``expires_at`` (mirrored to MongoDB by the route — see
 * ``summarization_inference_session_collection.py``). That means a refresh,
 * a different browser, or a different user all see the **same** countdown
 * synced to the actual queue TTL — the hook just trusts whatever the
 * backend returns and re-derives the remaining time from ``expires_at - now``.
 * Closing the Playground overlay unmounts this hook; remount hydrates
 * from the last server snapshot and always GETs /status again so an
 * active KEDA session restores Stop GPU + TTL without another Start click.
 */
const STATUS_POLL_INTERVAL_MS = 5_000;
const RUNNING_POLL_INTERVAL_MS = 30_000;

const SUMMARIZATION_INFERENCE_PATH = 'summarization-inference';

// Last /status (or /start) payload that reported an active session. The
// Playground overlay unmounts this hook when the user leaves for model
// download; without a snapshot the remount starts at ``idle`` and shows
// Start GPU until /status returns — or forever if that poll is skipped
// because ``enabled`` is still false on the first paint.
let sessionSnapshot = null;

const idleSnapshotState = () => ({
    status: 'idle',
    startedAt: null,
    expiresAt: null,
});

const stateFromSnapshot = () => {
    if (!sessionSnapshot?.session_active) {
        return idleSnapshotState();
    }
    const expiresAt = parseTimestamp(sessionSnapshot.expires_at);
    if (expiresAt != null && expiresAt <= Date.now()) {
        return idleSnapshotState();
    }
    return {
        status: sessionSnapshot.ready ? 'running' : 'starting',
        startedAt: parseTimestamp(sessionSnapshot.started_at),
        expiresAt,
    };
};

const snapshotFromPayload = (payload) => {
    if (!payload?.session_active) {
        return null;
    }
    return {
        session_active: true,
        ready: !!payload.ready,
        started_at: payload.started_at ?? null,
        expires_at: payload.expires_at ?? null,
    };
};

/** Test-only: drop the in-memory session so remount cases start clean. */
export const resetSummarizationInferenceSnapshot = () => {
    sessionSnapshot = null;
};

const buildUrl = (suffix) => `${get_base_url(origin)}/${SUMMARIZATION_INFERENCE_PATH}/${suffix}`;

const postSession = async (suffix, body) => {
    const response = await fetch(buildUrl(suffix), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        credentials: 'include',
    });
    if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        // 402 = the Starter workspace used up its free usage; the global gate
        // opens the Upgrade-to-Pro modal on top of the hook's own error state.
        announceIfFreeUsageExhausted(response.status, body?.detail);
        throw new Error(formatApiErrorDetail(body?.detail) || `Request failed: ${response.status}`);
    }
    return response.json();
};

const getStatus = async (params) => {
    const search = new URLSearchParams(params).toString();
    const response = await fetch(`${buildUrl('status')}?${search}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
    });
    if (!response.ok) {
        throw new Error(`Status request failed: ${response.status}`);
    }
    return response.json();
};

// Belt-and-suspenders: an ISO 8601 datetime without a timezone marker is
// parsed by ``new Date(...)`` as **local** time, which silently put the
// countdown in the past for any user not on UTC. The backend already
// emits ``+00:00`` via ``_to_utc_iso``; this guard makes sure a future
// regression on the server side can never re-introduce that bug. If the
// string lacks ``Z`` or a numeric offset suffix, we treat it as UTC.
const TZ_MARKER_RE = /[zZ]|[+-]\d{2}:?\d{2}$/;

const parseTimestamp = (value) => {
    if (!value) return null;
    const normalized = TZ_MARKER_RE.test(value) ? value : `${value}Z`;
    const ms = new Date(normalized).getTime();
    return Number.isFinite(ms) ? ms : null;
};

export const useSummarizationInference = ({ clientId, llmUsecaseId, enabled }) => {
    const initial = stateFromSnapshot();
    const [status, setStatus] = useState(initial.status);
    const [startedAt, setStartedAt] = useState(initial.startedAt);
    const [expiresAt, setExpiresAt] = useState(initial.expiresAt);
    const [now, setNow] = useState(() => Date.now());
    const pollTimerRef = useRef(null);
    const tickTimerRef = useRef(null);

    const clearPoll = () => {
        if (pollTimerRef.current) {
            clearTimeout(pollTimerRef.current);
            pollTimerRef.current = null;
        }
    };

    const applyServerStatus = useCallback((payload) => {
        if (!payload) {
            return;
        }
        const newStartedAt = parseTimestamp(payload.started_at);
        const newExpiresAt = parseTimestamp(payload.expires_at);
        sessionSnapshot = snapshotFromPayload(payload);

        if (!payload.session_active) {
            // KEDA scaled the deployment back to 0 (Stop, TTL, cluster
            // restart, etc.). Drop the local countdown so the UI returns
            // to "Start GPU" immediately.
            setStartedAt(null);
            setExpiresAt(null);
            setStatus('idle');
            return;
        }

        // Trust the server's timestamps. ``/status`` reads them from the
        // shared MongoDB session store, so refresh, cross-tab, and
        // cross-user views all converge on the same auto-stop time.
        // We use functional setState to avoid clobbering a value the
        // component just received from a faster ``/start`` response.
        // ``session_active`` alone is enough to restore Stop GPU + TTL
        // after a remount — do not wait for another Start GPU click.
        setStartedAt((current) => newStartedAt ?? current);
        setExpiresAt((current) => newExpiresAt ?? current);
        setStatus(payload.ready ? 'running' : 'starting');
    }, []);

    const pollStatus = useCallback(async () => {
        if (!enabled || !clientId || !llmUsecaseId) {
            return;
        }
        try {
            const payload = await getStatus({
                client_id: clientId,
                llm_usecase_id: llmUsecaseId,
            });
            applyServerStatus(payload);
            const interval = payload?.ready ? RUNNING_POLL_INTERVAL_MS : STATUS_POLL_INTERVAL_MS;
            clearPoll();
            pollTimerRef.current = setTimeout(pollStatus, interval);
        } catch (err) {
            console.warn('summarization-inference status poll failed', err);
            clearPoll();
            pollTimerRef.current = setTimeout(pollStatus, STATUS_POLL_INTERVAL_MS);
        }
    }, [enabled, clientId, llmUsecaseId, applyServerStatus]);

    useEffect(() => {
        if (!enabled || !clientId || !llmUsecaseId) {
            clearPoll();
            return undefined;
        }
        // Always hit /status on mount (and when the usecase becomes
        // known) so a return from model download restores running +
        // countdown from the live KEDA session.
        pollStatus();
        return clearPoll;
    }, [enabled, clientId, llmUsecaseId, pollStatus]);

    useEffect(() => {
        if (!enabled || !clientId || !llmUsecaseId) {
            return undefined;
        }
        const onVisible = () => {
            if (document.visibilityState === 'visible') {
                pollStatus();
            }
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, [enabled, clientId, llmUsecaseId, pollStatus]);

    useEffect(() => {
        if (status !== 'running' && status !== 'starting') {
            if (tickTimerRef.current) {
                clearInterval(tickTimerRef.current);
                tickTimerRef.current = null;
            }
            return undefined;
        }
        tickTimerRef.current = setInterval(() => setNow(Date.now()), 1000);
        return () => {
            if (tickTimerRef.current) {
                clearInterval(tickTimerRef.current);
                tickTimerRef.current = null;
            }
        };
    }, [status]);

    const start = useCallback(async () => {
        if (!clientId || !llmUsecaseId) {
            sparkToast.error('Missing usecase information');
            return;
        }
        setStatus('starting');
        try {
            const payload = await postSession('start', {
                client_id: clientId,
                llm_usecase_id: llmUsecaseId,
            });
            applyServerStatus({
                session_active: true,
                ready: !!payload?.ready,
                started_at: payload?.started_at,
                expires_at: payload?.expires_at,
            });
            clearPoll();
            pollStatus();
        } catch (err) {
            console.error('Failed to start summarization GPU', err);
            sparkToast.error(err?.message || 'Failed to start GPU');
            setStatus('error');
        }
    }, [clientId, llmUsecaseId, applyServerStatus, pollStatus]);

    const stop = useCallback(async () => {
        if (!clientId || !llmUsecaseId) {
            return;
        }
        const wasStarting = status === 'starting';
        setStatus('stopping');
        try {
            await postSession('stop', {
                client_id: clientId,
                llm_usecase_id: llmUsecaseId,
            });
            sessionSnapshot = null;
            setStatus('idle');
            setStartedAt(null);
            setExpiresAt(null);
            clearPoll();
            pollStatus();
        } catch (err) {
            console.error('Failed to stop summarization GPU', err);
            sparkToast.error(
                err?.message || (wasStarting ? 'Failed to cancel GPU start' : 'Failed to stop GPU')
            );
            setStatus('error');
        }
    }, [clientId, llmUsecaseId, pollStatus, status]);

    // ``expiresAt`` comes from the backend (via /start or /status); it
    // ticks down via the ``setNow`` interval. When unset (no active
    // session, or session active but server hasn't reported timestamps
    // yet) we report 0 so the button can hide the countdown chip.
    const remainingMs = expiresAt ? Math.max(0, expiresAt - now) : 0;
    const remainingLabel = formatRemaining(remainingMs);

    return {
        status,
        isRunning: status === 'running',
        isStarting: status === 'starting',
        isStopping: status === 'stopping',
        startedAt,
        expiresAt,
        remainingMs,
        remainingLabel,
        start,
        stop,
        // ``cancel`` is a semantic alias for ``stop`` used by the UI while
        // the session is still in the ``starting`` state (e.g. localhost
        // without KEDA, or the user changing their mind during cold start).
        cancel: stop,
        refresh: pollStatus,
    };
};

const pad = (value) => String(value).padStart(2, '0');

const formatRemaining = (ms) => {
    if (!ms || ms <= 0) {
        return '00:00';
    }
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) {
        return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    }
    return `${pad(minutes)}:${pad(seconds)}`;
};
